import { useState } from 'react'
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  Input,
  Switch,
  Tooltip,
  Typography,
} from '@material-tailwind/react'
import { PiInfoLight } from 'react-icons/pi'
import { useTranslation } from 'react-i18next'
import { colors } from '@material-tailwind/react/types/generic'
import { AccordionCaret } from '../components/AccordionCaret'
import { useFeatureAuth } from '../hooks/authorization'
import { FEATURES } from '../utils/constants/features.constants'
import { validateInputNumber } from '../utils/helpers/validate.helpers'
import { useDarkMode } from '../contexts/DarkModeContext'
import { ExperienceFormData } from './UploadExperience'

interface UploadExpAdvancedSettingsProps {
  formData: ExperienceFormData
  handleDataChange: <K extends keyof ExperienceFormData>(key: K, newValue: ExperienceFormData[K]) => void
  checkInputIsValid: <K extends keyof ExperienceFormData>(
    key: K,
    condition: boolean,
    fallbackVal?: ExperienceFormData[K] | undefined
  ) => void
}

const UploadExpAdvancedSettings: React.FC<UploadExpAdvancedSettingsProps> = ({
  formData,
  handleDataChange,
  checkInputIsValid,
}) => {
  const { t } = useTranslation()
  const { darkMode } = useDarkMode()
  const [open, setOpen] = useState(false)
  const experimentalFeats = useFeatureAuth(FEATURES.EXPERIMENTAL_FEATURES)

  return (
    <Accordion open={open} icon={<AccordionCaret rotate={open} />}>
      <AccordionHeader
        onClick={() => setOpen((prev) => !prev)}
        className="justify-center gap-4 dark:text-white dark:hover:text-gray-400"
      >
        {t('upload.experience.advancedSettings')}
      </AccordionHeader>
      <AccordionBody className="flex flex-col mt-4 gap-4 px-1">
        <div className="w-full flex flex-col md:flex-row justify-center md:items-center gap-4 mb-4 md:mb-0">
          <Input
            type="number"
            label={t('upload.experience.agamiRadius')}
            min={1}
            value={formData.default_radius ?? ''}
            onChange={(e) =>
              handleDataChange('default_radius', validateInputNumber(e.currentTarget.value ?? ''))
            }
            onBlur={() => checkInputIsValid('default_radius', (formData.default_radius ?? 0) > 0)}
            {...(darkMode && { color: 'white' })}
          />
          <Input
            type="number"
            label={t('upload.experience.agamiDeadband')}
            min={1}
            value={formData.default_deadband ?? ''}
            onChange={(e) =>
              handleDataChange('default_deadband', validateInputNumber(e.currentTarget.value ?? ''))
            }
            onBlur={() => checkInputIsValid('default_deadband', (formData.default_deadband ?? 0) > 0)}
            {...(darkMode && { color: 'white' })}
          />
          <Tooltip
            content={
              <div className="w-[300px]">
                <Typography variant="small" className="font-bold">
                  {t('upload.experience.radiusDeadbandDescription')}
                </Typography>
                <Typography variant="small">
                  <strong>
                    <em>{t('upload.experience.defaultRadiusDeadband')}</em>
                  </strong>
                </Typography>
              </div>
            }
          >
            <span>
              <PiInfoLight className="text-primary dark:text-base-gray-400" />
            </span>
          </Tooltip>
        </div>

        <div className="w-full flex flex-col md:flex-row justify-center md:items-center gap-4 mb-4 md:mb-0">
          <Input
            type="number"
            label={t('upload.experience.fadeInTime')}
            min={0}
            value={formData.fade_in_time ?? ''}
            onChange={(e) =>
              handleDataChange('fade_in_time', validateInputNumber(e.currentTarget.value ?? ''))
            }
            onBlur={() => checkInputIsValid('fade_in_time', (formData.fade_in_time ?? 0) >= 0)}
            {...(darkMode && { color: 'white' })}
          />
          <Input
            type="number"
            label={t('upload.experience.fadeOutTime')}
            min={0}
            value={formData.fade_out_time ?? ''}
            onChange={(e) =>
              handleDataChange('fade_out_time', validateInputNumber(e.currentTarget.value ?? ''))
            }
            onBlur={() => checkInputIsValid('fade_out_time', (formData.fade_out_time ?? 0) >= 0)}
            {...(darkMode && { color: 'white' })}
          />
          <Tooltip
            content={
              <div className="w-[300px]">
                <Typography variant="small" className="font-bold">
                  {t('upload.experience.fadeDescription')}
                </Typography>
                <Typography variant="small">
                  <strong>
                    <em>{t('upload.experience.defaultFadeTimes')}</em>
                  </strong>
                </Typography>
              </div>
            }
          >
            <span>
              <PiInfoLight className="text-primary dark:text-base-gray-400" />
            </span>
          </Tooltip>
        </div>

        <div className="flex flex-col md:flex-row justify-start md:items-center gap-4 mb-4 md:mb-0">
          <Input
            type="number"
            label={t('upload.experience.minAccuracy')}
            min={1}
            value={formData.accuracy ?? ''}
            onChange={(e) => handleDataChange('accuracy', validateInputNumber(e.currentTarget.value ?? ''))}
            onBlur={() => checkInputIsValid('accuracy', (formData.accuracy ?? 0) > 0)}
            {...(darkMode && { color: 'white' })}
          />
          <Tooltip
            content={
              <div className="w-[300px]">
                <Typography variant="small" className="font-bold">
                  {t('upload.experience.accuracyDescription')}
                </Typography>
                <Typography variant="small">
                  <strong>
                    <em>{t('upload.experience.defaultAccuracy')}</em>
                  </strong>
                </Typography>
              </div>
            }
            placement="right"
            interactive
          >
            <span>
              <PiInfoLight className="text-primary dark:text-base-gray-400" />
            </span>
          </Tooltip>
        </div>
        {experimentalFeats && (
          <div className="flex justify-start items-center gap-1">
            <Switch
              color={(darkMode ? 'accent' : 'primary') as colors}
              id="high_accuracy_switch"
              label={t('upload.experience.highAccuracy')}
              checked={Boolean(formData.enable_high_accuracy)}
              onChange={(e) => handleDataChange('enable_high_accuracy', e.currentTarget.checked)}
            />
            <Tooltip
              content={
                <div className="w-[300px]">
                  <Typography variant="small" className="font-bold">
                    {t('upload.experience.highAccuracyDescription')}
                  </Typography>
                  <Typography variant="small">
                    <strong>
                      <em>{t('upload.experience.defaultTrue')}</em>
                    </strong>
                  </Typography>
                </div>
              }
              placement="right"
              interactive
            >
              <span>
                <PiInfoLight className="text-primary dark:text-base-gray-400" />
              </span>
            </Tooltip>
          </div>
        )}
      </AccordionBody>
    </Accordion>
  )
}

export default UploadExpAdvancedSettings
